import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Send, Printer, CheckCircle, XCircle, AlertTriangle, Clock, RefreshCw, Paperclip, ChevronRight, ChevronLeft, Eye, Mail } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/*
  Procedura guidata «Verifica e invia» (richiesta della titolare, 08/09/2026).
  1. Anteprima: oggetto, testo e allegati esattamente come partiranno.
  2. Destinatari: canali attivi (PEC / email / stampa / bacheca), canale proposto per
     ciascuno (PEC se c'e', altrimenti email, altrimenti stampa), correggibile a mano.
  3. Riepilogo e conferma -> invio con ricevute.
  4. Esito per destinatario + PDF unico da stampare.
*/

const CANALI = [
  ['PEC', 'PEC', 'a chi ha la PEC in anagrafica'],
  ['EMAIL', 'Email ordinaria', 'a chi ha l\'email'],
  ['PRINT', 'Stampa', 'raccomandata A/R o consegna a mano: PDF unico'],
  ['APP', 'Bacheca', 'avviso nel portale e in «Le mie comunicazioni»'],
];

const ETICHETTA = {
  PEC: 'PEC', EMAIL: 'Email', PRINT: 'Stampa', APP: 'Bacheca', SKIP: 'Non inviare',
  RACCOMANDATA: 'Raccomandata A/R', MANO: 'A mano',
};

/*
  Anteprima del messaggio (richiesta della titolare, 11/09/2026): mostra la PEC o l'email
  esattamente come la ricevera' quel condomino, prima di confermare l'invio a tutti.
  Non chiama il server: usa i dati del piano di invio, che sono gli stessi che verranno spediti.
*/
export const AnteprimaMessaggio = ({ riga, plan, onChiudi }) => {
  if (!riga || !plan) return null;
  const viaPec = riga.channel === 'PEC';
  const destinatario = viaPec ? riga.pec : riga.email;
  const mittente = (plan.senders || {})[riga.channel];
  return (
    <div className="border-2 border-blue-200 rounded bg-blue-50/40" data-testid="anteprima-messaggio">
      <div className="flex items-center justify-between px-3 py-2 border-b bg-blue-100/60">
        <div className="flex items-center gap-2 text-sm font-medium text-blue-900">
          <Mail className="h-4 w-4" />
          Come arriva a {riga.name}
          <Badge variant="outline">{viaPec ? 'PEC' : 'Email'}</Badge>
        </div>
        <Button variant="ghost" size="sm" onClick={onChiudi} data-testid="chiudi-anteprima">Chiudi</Button>
      </div>
      <div className="p-3 space-y-2 bg-white">
        <div className="text-sm grid grid-cols-[70px_1fr] gap-x-2 gap-y-1">
          <span className="text-gray-500">Da</span>
          <span>{mittente || (viaPec ? 'casella PEC del condominio' : 'casella email del condominio')}</span>
          <span className="text-gray-500">A</span>
          <span className="font-medium">{destinatario || <span className="text-red-600">recapito mancante</span>}</span>
          <span className="text-gray-500">Oggetto</span>
          <span className="font-medium">{plan.subject}</span>
        </div>
        <div className="border rounded bg-gray-50 p-3 max-h-[300px] overflow-y-auto">
          <pre className="text-xs whitespace-pre-wrap font-sans leading-relaxed">{plan.body}</pre>
        </div>
        <div className="text-sm">
          <span className="text-gray-500">Allegati ({(riga.attachments || []).length}):</span>
          {(riga.attachments || []).length === 0 && <span className="text-amber-700"> nessuno</span>}
          <ul className="mt-1 space-y-0.5">
            {(riga.attachments || []).map(a => (
              <li key={a.id} className="flex items-center gap-1.5 text-xs">
                <Paperclip className="h-3 w-3 text-gray-400" />
                <span className={a.personal ? 'text-green-700 font-medium' : ''}>{a.title}</span>
                {a.personal && <Badge variant="outline" className="text-[10px] py-0">personale per l’unità</Badge>}
              </li>
            ))}
          </ul>
        </div>
        {(riga.note || []).length > 0 && (
          <div className="text-xs text-amber-700">{(riga.note || []).join('; ')}</div>
        )}
        <p className="text-[11px] text-gray-500 border-t pt-2">
          Il testo è identico per tutti: cambia solo l’allegato personale dell’unità. Il numero di protocollo
          nell’oggetto viene assegnato al momento dell’invio.
        </p>
      </div>
    </div>
  );
};

const StatoIcona = ({ status }) => {
  if (status === 'SENT') return <CheckCircle className="h-4 w-4 text-green-600" />;
  if (status === 'FAILED') return <XCircle className="h-4 w-4 text-red-600" />;
  if (status === 'SKIPPED') return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
  return <Clock className="h-4 w-4 text-gray-400" />;
};

export async function scaricaPdfDaStampare({ condominiumId, commId, token, body }) {
  const res = await fetch(`${API_URL}/api/condominiums/${condominiumId}/communications-center/${commId}/print-pdf`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Errore nella generazione del PDF';
    try { msg = (await res.json()).detail || msg; } catch (e) { /* corpo non JSON */ }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `da_stampare_${commId.slice(0, 8)}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
  return { letters: res.headers.get('X-Letters'), missing: res.headers.get('X-Missing') };
}

export default function SendCommunicationWizard({ open, onOpenChange, condominiumId, communication, token, onDone }) {
  const [step, setStep] = useState(1);
  const [docs, setDocs] = useState([]);
  const [docSearch, setDocSearch] = useState('');
  const [attachments, setAttachments] = useState([]);       // [{document_id, personal}]
  const [channels, setChannels] = useState({ PEC: true, EMAIL: true, PRINT: true, APP: true });
  const [includeTenants, setIncludeTenants] = useState(false);
  const [overrides, setOverrides] = useState({});
  const [plan, setPlan] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [printing, setPrinting] = useState(false);
  const [anteprima, setAnteprima] = useState(null);   // riga di cui si guarda il messaggio

  const comm = communication;

  // reset a ogni apertura
  useEffect(() => {
    if (!open) return;
    setStep(1); setPlan(null); setResult(null); setOverrides({}); setDocSearch(''); setAnteprima(null);
    // gli allegati già agganciati alla comunicazione (es. il PDF di convocazione) partono selezionati
    setAttachments((comm?.attachments || []).filter(Boolean).map((id) => ({ document_id: id, personal: false })));
    setIncludeTenants(comm?.comm_type !== 'ASSEMBLEA');
    setChannels({ PEC: true, EMAIL: true, PRINT: true, APP: true });
  }, [open, comm?.id, comm?.comm_type, comm?.attachments, comm?.attachments_personal]);

  // documenti in Archivio (solo quelli non «per unita'»: le lettere personali si risolvono da sole)
  useEffect(() => {
    if (!open || !token || !condominiumId) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/condominiums/${condominiumId}/documents?limit=500`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setDocs((Array.isArray(data) ? data : []).filter(d => d.visibility !== 'UNIT_ONLY'));
        }
      } catch (e) {
        toast.error('Archivio non raggiungibile');
      }
    })();
  }, [open, token, condominiumId]);

  const body = useMemo(() => ({
    channels: Object.entries(channels).filter(([, v]) => v).map(([k]) => k),
    overrides,
    attachments,
    include_tenants: includeTenants,
  }), [channels, overrides, attachments, includeTenants]);

  const fetchPlan = useCallback(async () => {
    if (!comm?.id) return;
    setLoadingPlan(true);
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condominiumId}/communications-center/${comm.id}/send-plan`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setPlan(await res.json());
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Errore nel calcolo del piano di invio');
      }
    } catch (e) {
      toast.error('Errore di connessione');
    } finally {
      setLoadingPlan(false);
    }
  }, [comm?.id, condominiumId, token, body]);

  // il piano si ricalcola quando cambiano canali, allegati, conduttori o scelte per persona
  useEffect(() => {
    if (open && step >= 2) fetchPlan();
  }, [open, step, fetchPlan]);

  const docsFiltrati = useMemo(() => {
    const q = docSearch.trim().toLowerCase();
    let lista = docs;
    if (!q && comm?.comm_type === 'ASSEMBLEA') {
      const conv = docs.filter(d => d.category === 'CONVOCAZIONE_ASSEMBLEA');
      if (conv.length) lista = conv;
    }
    if (q) lista = docs.filter(d => (d.title || '').toLowerCase().includes(q) || (d.file_name || '').toLowerCase().includes(q));
    return lista.slice(0, 40);
  }, [docs, docSearch, comm?.comm_type]);

  const toggleDoc = (doc) => {
    setAttachments(prev => prev.some(a => a.document_id === doc.id)
      ? prev.filter(a => a.document_id !== doc.id)
      : [...prev, { document_id: doc.id, personal: false }]);
  };
  const togglePersonal = (docId) => {
    setAttachments(prev => prev.map(a => a.document_id === docId ? { ...a, personal: !a.personal } : a));
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condominiumId}/communications-center/${comm.id}/send`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...body, confirm: true }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setResult(data);
        setStep(4);
        toast.success(`Inviato: ${data.stats?.sent || 0} via posta, ${data.stats?.print || 0} da stampare`);
        if (onDone) onDone(data);
      } else {
        toast.error(data.detail || 'Errore nell\'invio');
      }
    } catch (e) {
      toast.error('Errore di connessione');
    } finally {
      setSending(false);
    }
  };

  // Foglio di presa visione per la portineria: tutti i condomini, canale di invio, spazio firma
  const [presaVisione, setPresaVisione] = useState(false);
  const handlePresaVisione = async () => {
    setPresaVisione(true);
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condominiumId}/communications-center/${comm.id}/presa-visione-pdf`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Errore nella generazione del foglio');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `presa_visione_${comm.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`Foglio di presa visione pronto (${res.headers.get('X-Rows') || '?'} condomini)`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setPresaVisione(false);
    }
  };

  const handlePrint = async () => {
    setPrinting(true);
    try {
      const info = await scaricaPdfDaStampare({ condominiumId, commId: comm.id, token, body });
      toast.success(`PDF pronto: ${info.letters || '?'} lettere${info.missing && info.missing !== '0' ? `, ${info.missing} senza PDF` : ''}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setPrinting(false);
    }
  };

  const counts = plan?.counts || {};
  const attivi = body.channels;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Verifica e invia
            <span className="font-mono text-xs text-gray-500 ml-2">{comm?.protocol_number}</span>
          </DialogTitle>
          <DialogDescription>
            {['1 · Anteprima del contenuto', '2 · Destinatari e canali', '3 · Riepilogo e conferma', '4 · Esito'].map((t, i) => (
              <span key={t} className={`mr-3 ${step === i + 1 ? 'font-semibold text-gray-900' : ''}`}>{t}</span>
            ))}
          </DialogDescription>
        </DialogHeader>

        {/* ---------- 1. ANTEPRIMA ---------- */}
        {step === 1 && comm && (
          <div className="space-y-4">
            <div className="border rounded-lg p-4 bg-white">
              <div className="text-xs text-gray-500">Oggetto (email e PEC)</div>
              <div className="font-medium">[{comm.protocol_number || 'Comunicazione'}] {comm.title}</div>
              <div className="text-xs text-gray-500 mt-3">Testo</div>
              <p className="whitespace-pre-wrap text-sm" data-testid="anteprima-testo">{comm.content}</p>
            </div>

            <div>
              <Label className="flex items-center gap-2"><Paperclip className="h-4 w-4" /> Allegati dall'Archivio</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Scegli i documenti da allegare a PEC ed email e da stampare. Spunta «personale» sul documento generico
                se in Archivio esistono le lettere per unità (visibili solo al condomino): a ogni persona andrà la sua.
              </p>
              <Input placeholder="Cerca per titolo…" value={docSearch} onChange={e => setDocSearch(e.target.value)} className="mb-2" />
              <div className="max-h-[220px] overflow-y-auto border rounded">
                <Table>
                  <TableBody>
                    {docsFiltrati.map(d => {
                      const sel = attachments.find(a => a.document_id === d.id);
                      return (
                        <TableRow key={d.id} className={sel ? 'bg-blue-50' : ''}>
                          <TableCell className="w-8">
                            <input type="checkbox" checked={!!sel} onChange={() => toggleDoc(d)} data-testid={`allegato-${d.id}`} />
                          </TableCell>
                          <TableCell className="text-sm">
                            <div>{d.title}</div>
                            <div className="text-xs text-gray-500">{d.file_name} · {d.category} · {d.visibility}</div>
                          </TableCell>
                          <TableCell className="w-44 text-xs">
                            {sel && (
                              <label className="flex items-center gap-1 cursor-pointer">
                                <input type="checkbox" checked={!!sel.personal} onChange={() => togglePersonal(d.id)} />
                                personale per unità
                              </label>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {docsFiltrati.length === 0 && (
                      <TableRow><TableCell className="text-sm text-gray-500">Nessun documento</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              {attachments.length > 0 && (
                <div className="text-xs text-gray-600 mt-2">
                  Allegati scelti: {attachments.map(a => {
                    const d = docs.find(x => x.id === a.document_id);
                    return `${d?.title || a.document_id}${a.personal ? ' (personale)' : ''}`;
                  }).join(' · ')}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ---------- 2. DESTINATARI ---------- */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-4 items-center">
              {CANALI.map(([k, lbl, descr]) => (
                <label key={k} className="flex items-center gap-2 cursor-pointer text-sm" title={descr}>
                  <input type="checkbox" checked={!!channels[k]} onChange={e => setChannels({ ...channels, [k]: e.target.checked })} data-testid={`canale-invio-${k}`} />
                  {lbl}
                </label>
              ))}
              <label className="flex items-center gap-2 cursor-pointer text-sm ml-auto">
                <input type="checkbox" checked={includeTenants} onChange={e => setIncludeTenants(e.target.checked)} />
                includi conduttori (affittuari)
              </label>
              <Button variant="ghost" size="sm" onClick={fetchPlan} disabled={loadingPlan}>
                <RefreshCw className={`h-4 w-4 ${loadingPlan ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Regola proposta: PEC se c'è; altrimenti email; altrimenti stampa (raccomandata A/R se c'è l'indirizzo, sennò consegna a mano).
              Puoi cambiare il canale persona per persona, e scegliere tu se stampare per la <b>raccomandata</b> o per la
              <b> consegna a mano</b> in portineria, anche per chi ha un indirizzo. Con «Vedi» controlli il messaggio
              esatto che riceverà quella persona prima di inviare a tutti.
            </p>
            {plan?.warnings?.map(w => (
              <div key={w} className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 flex gap-2"><AlertTriangle className="h-4 w-4 mt-0.5" />{w}</div>
            ))}
            {plan?.generic_attachments?.some(a => a.personal && a.personal_found === 0) && (
              <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                Nessuna lettera personale trovata per un allegato marcato «personale»: a tutti andrà il documento generico.
              </div>
            )}
            {anteprima && (
              <AnteprimaMessaggio
                riga={(plan?.rows || []).find(x => x.person_id === anteprima)}
                plan={plan}
                onChiudi={() => setAnteprima(null)}
              />
            )}
            <div className="max-h-[420px] overflow-y-auto border rounded">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Unità</TableHead>
                    <TableHead>Destinatario</TableHead>
                    <TableHead>Recapiti</TableHead>
                    <TableHead>Canale</TableHead>
                    <TableHead>Allegati</TableHead>
                    <TableHead>Messaggio</TableHead>
                    <TableHead>Note</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(plan?.rows || []).map(r => (
                    <TableRow key={r.person_id} className={r.channel === 'SKIP' ? 'opacity-60' : ''}>
                      <TableCell className="text-xs font-mono">{(r.units || []).join(', ')}</TableCell>
                      <TableCell className="text-sm">
                        {r.name}
                        <div className="text-xs text-gray-500">{(r.roles || []).map(x => ({ OWNER: 'propr.', CO_OWNER: 'compropr.', BARE_OWNER: 'nudo propr.', USUFRUCTUARY: 'usufr.', TENANT: 'conduttore' }[x] || x)).join(', ')}</div>
                      </TableCell>
                      <TableCell className="text-xs">
                        {r.pec && <div>PEC: {r.pec}</div>}
                        {r.email && <div>Email: {r.email}</div>}
                        {r.address && <div>{r.address}</div>}
                        {!r.pec && !r.email && !r.address && <span className="text-gray-400">nessuno</span>}
                      </TableCell>
                      <TableCell>
                        <select
                          className="border rounded px-2 py-1 text-sm bg-white"
                          value={overrides[r.person_id] || (r.channel === 'PRINT' ? `PRINT:${r.print_mode || 'MANO'}` : r.channel)}
                          onChange={e => {
                            const v = e.target.value;
                            setOverrides(prev => {
                              const n = { ...prev };
                              // la scelta di stampa e' sempre esplicita: la si tiene come forzatura
                              if (!v.includes(':') && (v === r.proposed || (v === 'SKIP' && !r.proposed))) delete n[r.person_id];
                              else n[r.person_id] = v;
                              return n;
                            });
                          }}
                          data-testid={`canale-persona-${r.person_id}`}
                        >
                          {attivi.includes('PEC') && <option value="PEC" disabled={!r.pec}>PEC</option>}
                          {attivi.includes('EMAIL') && <option value="EMAIL" disabled={!r.email}>Email</option>}
                          {attivi.includes('PRINT') && <option value="PRINT:MANO">Stampa · a mano (portineria)</option>}
                          {attivi.includes('PRINT') && <option value="PRINT:RACCOMANDATA" disabled={!r.address}>Stampa · raccomandata A/R{r.address ? '' : ' (manca l’indirizzo)'}</option>}
                          <option value="SKIP">Non inviare{r.bacheca ? ' (solo bacheca)' : ''}</option>
                        </select>
                        {r.proposed && r.channel !== r.proposed && (
                          <div className="text-[11px] text-gray-500">proposto: {ETICHETTA[r.proposed]}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        {(r.attachments || []).map(a => (
                          <div key={a.id} className={a.personal ? 'text-green-700' : ''}>{a.personal ? '★ ' : ''}{a.title}</div>
                        ))}
                      </TableCell>
                      <TableCell>
                        {(r.channel === 'PEC' || r.channel === 'EMAIL') ? (
                          <Button variant="outline" size="sm" className="text-xs h-7"
                            onClick={() => setAnteprima(r.person_id)}
                            data-testid={`anteprima-messaggio-${r.person_id}`}
                            title="Vedi esattamente il messaggio che ricevera' questa persona">
                            <Eye className="h-3.5 w-3.5 mr-1" />Vedi
                          </Button>
                        ) : <span className="text-xs text-gray-400">cartaceo</span>}
                      </TableCell>
                      <TableCell className="text-xs text-amber-700">{(r.note || []).join('; ')}</TableCell>
                    </TableRow>
                  ))}
                  {!loadingPlan && plan && plan.rows.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-sm text-gray-500">Nessun destinatario con titolarità attiva</TableCell></TableRow>
                  )}
                  {loadingPlan && !plan && (
                    <TableRow><TableCell colSpan={7} className="text-sm text-gray-500">Calcolo in corso…</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* ---------- 3. RIEPILOGO ---------- */}
        {step === 3 && plan && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[['PEC', counts.PEC, 'via PEC'], ['EMAIL', counts.EMAIL, 'via email'], ['PRINT', counts.PRINT, `da stampare (${counts.RACCOMANDATA || 0} racc., ${counts.MANO || 0} a mano)`],
                ['APP', counts.APP, 'in bacheca'], ['SKIP', counts.SKIP, 'non inviati']].map(([k, n, lbl]) => (
                <div key={k} className="text-center p-3 bg-gray-50 rounded border">
                  <div className="text-2xl font-bold">{n || 0}</div>
                  <div className="text-xs text-gray-600">{lbl}</div>
                </div>
              ))}
            </div>
            <div className="border rounded p-3 text-sm bg-white">
              <div><span className="text-gray-500">Oggetto:</span> {plan.subject}</div>
              <div className="mt-1"><span className="text-gray-500">Allegati:</span> {plan.generic_attachments.length === 0 ? 'nessuno' :
                plan.generic_attachments.map(a => `${a.title}${a.personal ? ` (personale: ${a.personal_found} lettere trovate)` : ''}`).join(' · ')}</div>
              <div className="mt-1"><span className="text-gray-500">Canali attivi:</span> {plan.channels.map(c => ETICHETTA[c]).join(', ')}</div>
            </div>
            {anteprima && (
              <AnteprimaMessaggio
                riga={(plan?.rows || []).find(x => x.person_id === anteprima)}
                plan={plan}
                onChiudi={() => setAnteprima(null)}
              />
            )}
            <div className="max-h-[260px] overflow-y-auto border rounded">
              <Table>
                <TableBody>
                  {plan.rows.filter(r => r.channel !== 'SKIP').map(r => (
                    <TableRow key={r.person_id}>
                      <TableCell className="text-xs font-mono w-16">{(r.units || []).join(', ')}</TableCell>
                      <TableCell className="text-sm">{r.name}</TableCell>
                      <TableCell className="text-xs"><Badge variant="outline">{ETICHETTA[r.channel]}{r.print_mode ? ` · ${ETICHETTA[r.print_mode]}` : ''}</Badge></TableCell>
                      <TableCell className="text-xs text-gray-600">{r.channel === 'PEC' ? r.pec : r.channel === 'EMAIL' ? r.email : (r.address || 'portineria')}</TableCell>
                      <TableCell className="w-20">
                        {(r.channel === 'PEC' || r.channel === 'EMAIL') && (
                          <Button variant="outline" size="sm" className="text-xs h-7"
                            onClick={() => setAnteprima(r.person_id)}
                            data-testid={`anteprima-riepilogo-${r.person_id}`}>
                            <Eye className="h-3.5 w-3.5 mr-1" />Vedi
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
              Confermando, PEC ed email partono subito e non si possono richiamare. Le lettere da stampare restano
              «in attesa di consegna» finché non le segni consegnate. La comunicazione passa da bozza a inviata.
            </div>
          </div>
        )}

        {/* ---------- 4. ESITO ---------- */}
        {step === 4 && result && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[['sent', 'inviati', 'text-green-600'], ['failed', 'falliti', 'text-red-600'], ['print', 'da stampare', 'text-blue-600'], ['skipped', 'saltati', 'text-gray-600']].map(([k, lbl, cls]) => (
                <div key={k} className="text-center p-3 bg-gray-50 rounded border">
                  <div className={`text-2xl font-bold ${cls}`}>{result.stats?.[k] || 0}</div>
                  <div className="text-xs text-gray-600">{lbl}</div>
                </div>
              ))}
            </div>
            <div className="max-h-[320px] overflow-y-auto border rounded">
              <Table>
                <TableHeader>
                  <TableRow><TableHead>Unità</TableHead><TableHead>Destinatario</TableHead><TableHead>Canale</TableHead><TableHead>Esito</TableHead><TableHead>Dettaglio</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {(result.results || []).map(r => (
                    <TableRow key={r.person_id}>
                      <TableCell className="text-xs font-mono">{(r.units || []).join(', ')}</TableCell>
                      <TableCell className="text-sm">{r.name}</TableCell>
                      <TableCell className="text-xs">{ETICHETTA[r.channel] || r.channel}{r.print_mode ? ` · ${ETICHETTA[r.print_mode]}` : ''}</TableCell>
                      <TableCell><StatoIcona status={r.status} /></TableCell>
                      <TableCell className="text-xs text-gray-600">{r.error || r.note || r.to || (r.status === 'QUEUED' ? 'in attesa di consegna' : '')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 flex-wrap">
          {step > 1 && step < 4 && (
            <Button variant="outline" onClick={() => setStep(step - 1)}><ChevronLeft className="h-4 w-4 mr-1" />Indietro</Button>
          )}
          {(step === 2 || step === 3 || step === 4) && plan && (
            <Button variant="outline" onClick={handlePresaVisione} disabled={presaVisione} data-testid="scarica-presa-visione" title="Elenco di tutti i condomini con canale di invio e spazio per la firma, da lasciare in portineria">
              <Printer className="h-4 w-4 mr-1" />{presaVisione ? 'Preparo…' : 'Foglio presa visione'}
            </Button>
          )}
          {(step === 2 || step === 3 || step === 4) && (counts.PRINT > 0 || result?.stats?.print > 0) && (
            <Button variant="outline" onClick={handlePrint} disabled={printing} data-testid="scarica-pdf-stampa" title="Foglio di presa visione + registro di consegna + lettere e allegati di chi va in raccomandata o a mano">
              <Printer className="h-4 w-4 mr-1" />{printing ? 'Preparo il PDF…' : 'PDF da stampare'}
            </Button>
          )}
          {step === 1 && (
            <Button onClick={() => setStep(2)} data-testid="wizard-avanti-1">Destinatari<ChevronRight className="h-4 w-4 ml-1" /></Button>
          )}
          {step === 2 && (
            <Button onClick={() => setStep(3)} disabled={!plan || loadingPlan || plan.rows.length === 0} data-testid="wizard-avanti-2">Riepilogo<ChevronRight className="h-4 w-4 ml-1" /></Button>
          )}
          {step === 3 && (
            <Button onClick={handleSend} disabled={sending} data-testid="wizard-conferma-invio">
              <Send className="h-4 w-4 mr-1" />{sending ? 'Invio in corso…' : 'Conferma e invia'}
            </Button>
          )}
          {step === 4 && (
            <Button onClick={() => onOpenChange(false)}>Chiudi</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
